<template>
  <div class="lib__form" data-cy="smart-meter-connection-form">
    <header class="lib__form__header">
      <!-- Module TITRE -->
      <main-title>{{ $t('common.smartMeterConnection') }}</main-title>
      <!-- Fin Module TITRE -->

      <!-- Module BreadCrumb -->
      <breadcrumb :currentStep="currentStep" :lastStep="lastStep" :lastStepSeen="lastStepSeen" :stepDefinitions="stepDefinitions" @goToStep="goToStepUsingBreadCrumb" @reset="resetForm" :isLastStepActive="false"></breadcrumb>
      <!-- Fin Module BreadCrumb -->
    </header>

    <ApiErrors />

    <back-to-summary :summaryStepNumber="5" :isButtonShowed="currentStep < 5 && lastStepSeen == 5" @backToSummary="goToStepWithValidation" />

    <!-- Module Step1 : Identification -->
    <IdentificationStep @canContinue="canContinue = $event" @next="nextStep" ref="step1" v-if="currentStep == 1"></IdentificationStep>
    <!-- Fin Module Step1 : Identification -->

    <!-- Module Step2 : Production d'énergie -->
    <EnergyProductionStep @canContinue="canContinue = $event" @next="nextStep" @previous="previousStep" ref="step2" v-if="currentStep == 2"></EnergyProductionStep>
    <!-- Fin Module Step2 : Production d'énergie -->

    <!-- Module Step3 : Contact -->
    <ContactInfoStep @canContinue="canContinue = $event" @next="nextStep" @previous="previousStep" ref="step3" v-if="currentStep == 3"></ContactInfoStep>
    <!-- Fin Module Step3 : Contact -->

    <!-- Module Step4 : TVA -->
    <VatInfoStep @canContinue="canContinue = $event" @next="nextStep" @previous="previousStep" ref="step4" v-if="currentStep == 4"></VatInfoStep>
    <!-- Fin Module Step4 : TVA -->

    <!-- Module Step5 : Résumé -->
    <SummaryStep :confirmation="lastStepSeen >= 8" :readOnly="lastStepSeen > 6" @canContinue="canContinue = $event" @goToStep="goToStep" @next="nextStep" @previous="previousStep" ref="step5" v-if="currentStep == 5"></SummaryStep>
    <!-- Fin Module Step5 : Résumé -->

    <!-- Module Step6 : Appointment Date Preselection -->
    <AppointmentDatePreselectionStep @canContinue="canContinue = $event" @next="nextStep" @previous="previousStep" @loading="setLoading" ref="step6" v-if="currentStep == 6"></AppointmentDatePreselectionStep>
    <!-- Fin Module Step6 : Appointment Date Preselection -->

    <!-- Module Step7 : Appointment -->
    <AppointmentStep @canContinue="canContinue = $event" @next="nextStep" @previous="previousStep" ref="step7" v-if="currentStep == 7" />
    <!-- Fin Module Step7 : Appointment -->

    <!-- Module Step8 : Confirmation -->
    <SendConfirmation :contact-info="contactInfoState" :title="$t('common.smartMeterConnectionRequest')" v-if="currentStep == 8" />
    <!-- Fin Module Step8 : Confirmation -->

    <StepActions :can-continue="canContinue" :has-next="hasNext" :has-previous="hasPrevious" :has-print="false" :has-reset="false" :loading="loading" @next="tryNextStep" @previous="tryPreviousStep" @reset="resetForm" v-if="showStepAction" :nextLabel="nextLabel"></StepActions>
  </div>
</template>

<script lang="ts">
import { Component, Watch } from 'vue-property-decorator'
import {
  ContactInfo,
  EnergyProductionInfoModel,
  RestrictedPowerLevel,
  RestrictedWorkRequestService,
  RestrictedWorkRequestTopic,
  RestrictedWorkRequestType,
  WorkFileCancellationRequest
} from '@/models'
import StepActions from '@/components/shared/StepActions.vue'
import SendConfirmation from '@/components/steps/smart-meter-connection/SendConfirmation.vue'
import Step from '@/components/steps/Step'
import IdentificationStep from '@/components/steps/smart-meter-connection/IdentificationStep.vue'
import EnergyProductionStep from '@/components/steps/smart-meter-connection/EnergyProductionStep.vue'
import ContactInfoStep from '@/components/steps/smart-meter-connection/ContactInfoStep.vue'
import VatInfoStep from '@/components/steps/smart-meter-connection/VatInfoStep.vue'
import AppointmentDatePreselectionStep
  from '@/components/steps/smart-meter-connection/AppointmentDatePreselectionStep.vue'
import SummaryStep from '@/components/steps/smart-meter-connection/SummaryStep.vue'
import AppointmentStep from '@/components/steps/smart-meter-connection/AppointmentStep.vue'
import { State, Mutation, Action } from 'vuex-class'
import { Form } from './Form'
import { StepDefinition } from '@ores/vue-library'
import ApiErrors from '@/components/display/ApiErrors.vue'
import { Route } from 'vue-router'
import { removeInterceptors } from '@/plugins/http-interceptors-global'
import { addSmartMeterResponseInterceptors } from '@/plugins/http-response-interceptors-smart-meter'
import store from '@/store/index'
import { contactService } from '@/services'

const initialSteps: StepDefinition[] = [
  new StepDefinition('identification', 1),
  new StepDefinition('request', 2),
  new StepDefinition('contact', 3, [new StepDefinition('vat', 4)]),
  new StepDefinition('summary', 5),
  new StepDefinition('appointment', 6, [new StepDefinition('', 7)]),
  new StepDefinition('confirm', 8)
]

@Component({
  components: {
    StepActions,
    IdentificationStep,
    EnergyProductionStep,
    ContactInfoStep,
    VatInfoStep,
    AppointmentDatePreselectionStep,
    SummaryStep,
    AppointmentStep,
    SendConfirmation,
    ApiErrors
  },
  name: 'smart-meter-connection'
})
export default class SmartMeterConnection extends Form {
  //#region [Data]
  public showStepAction: boolean = true
  public stepDefinitions: StepDefinition[] = initialSteps.map((s) => s)
  public isIdentified: boolean = false

  @State('contactInfo', { namespace: 'smartMeterConnection/contactInfo' })
  public contactInfoState!: ContactInfo | null

  @State('noAppointment', { namespace: 'smartMeterConnection' })
  public noAppointment!: boolean

  @State('fileNumber', { namespace: 'smartMeterConnection/stakeholder' })
  public fileNumber!: string | null

  @State('energyProductionInfo', { namespace: 'smartMeterConnection/energyProduction' })
  public energyProductionInfoState!: EnergyProductionInfoModel
  //#endregion

  //#region [Computed]
  public get hasPrevious(): boolean {
    return this.lastStepSeen < 8 && this.currentStep !== 7 && this.currentStep > 1
  }

  public get hasNext(): boolean {
    if (this.currentStep === 1) {
      return this.canContinue
    }
    return this.lastStepSeen < 8
  }

  private get nextLabel(): string | null {
    if (this.currentStep === 5) {
      return this.$t('smartMeter.buttons.summaryStep').toString()
    }
    if (this.currentStep === 6) {
      return this.$t('smartMeter.buttons.appointmentDatePreselectionStep').toString()
    }
    return null
  }

  //#endregion

  //#region [Mutation]
  @Mutation('STORE', { namespace: 'smartMeterConnection' })
  public store!: (index: boolean | null) => void
  //#endregion

  //#region [Method]
  @Action('validate', { namespace: 'smartMeterConnection/vatInfo' })
  public validateVatInfoSelection!: (contactInfo: ContactInfo) => Promise<boolean>

  public mounted() {
    this.currentStep = 1
    this.lastStepSeen = 1
    this.stepToGoAfterValidation = 5
    this.stepsToValidate = [3]

    this.onLangChanged(this.lang)

    if (this.interceptorId !== null) {
      removeInterceptors(this.$api, this.interceptorId)
    }
    const interceptorId = addSmartMeterResponseInterceptors(this.$api, store)
    this.setInterceptorId(interceptorId)

    // Check maintenance
    this.$api.get('api/maintenanceManagement/smart-meter-request')
  }

  public getStepComponent(step: number): Step | null {
    return (this.$refs[`step${this.currentStep}`] as Step) || null
  }

  public resetForm() {
    this.currentStep = 1
    this.lastStepSeen = 1
    this.$store.dispatch('smartMeterConnection/reset')
    this.$nextTick(() => (this.currentStep = 1))
  }

  // This method is called whenever url changes.
  public async beforeRouteLeave(to: Route, from: Route, continueNavigation: any) {
    if (to.meta?.lang !== from.meta?.lang) {
      // url changes when user uses language switch,
      // allow navigation to target url.
      continueNavigation(true)
      return
    }
    if (this.shouldAskConfirmCloseWorkFile()) {
      // User leaves a specific step and this ask for user confirmation
      const answer = this.confirmCloseWorkFile()
      if (answer) {
        // User confirmed,
        // Leaving this step should close the work file and navigate to previous step
        await this.closeWorkFile()
        await this.previousStep(true)
        // Prevent url history navigation
        continueNavigation(false)
        return
      } else {
        // User didn't confirm, stay on page,
        // Prevent url history navigation
        continueNavigation(false)
      }
    } else {
      // No user confirmation required
      // Special case : step Identification and step for outside hours
      if (this.currentStep < 2) {
        continueNavigation(true)
        return
      } else {
        // Navigate to previous step
        this.previousStep(true)
        // Prevent url history navigation
        continueNavigation(false)
      }
    }
  }

  public async goToStepUsingBreadCrumb(step: number) {
    if (this.shouldAskConfirmCloseWorkFile()) {
      const answer = this.confirmCloseWorkFile()
      if (answer) {
        await this.closeWorkFile()
        this.goToStepWithValidation(step)
        return
      } else {
        return
      }
    }
    this.goToStepWithValidation(step)
  }

  public shouldAskConfirmCloseWorkFile(): boolean {
    return this.currentStep === 7 && !!this.fileNumber // null when file creation error occurred
  }

  public confirmCloseWorkFile(): boolean {
    return window.confirm(this.$t('appointment.leavePageMessage').toString())
  }

  public async closeWorkFile() {
    const token = sessionStorage.getItem('token')
    const data: WorkFileCancellationRequest = {
      workFileReference: this.fileNumber!,
      powerLevel: RestrictedPowerLevel.Low,
      workRequestType: RestrictedWorkRequestType.Electricity,
      service: this.energyProductionInfoState.hasEnergyProduction
        ? RestrictedWorkRequestService.ElectricityProductionCommissioning
        : RestrictedWorkRequestService.MeterReplacementBySmartMeter,
      topic: RestrictedWorkRequestTopic.SmartMeter,
      contactInfo: contactService.getContact(this.contactInfoState!),
      language: 'fr'
    }

    if (!token) {
      throw new Error('No token found in sessionStorage')
    }
    
    await this.$api.post(`api/WorksRequest/work-file/${this.fileNumber}/close`, data, {
      headers: {
        Authorization: token
      }
    })
  }

  public setLoading(value: boolean) {
    this.loading = value ? 'next' : null
  }

  //#endregion

  //#region [Watch]
  @Watch('contactInfoState', { deep: true })
  public async onContactInfoChanged(contactInfo: ContactInfo | null) {
    if (contactInfo === null) {
      return
    }

    this.ensureLastStep(4, await this.validateVatInfoSelection(contactInfo))
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('afterEnsured'))
    }, 100)
  }

  //#endregion
}
</script>

<style>
.cw__smart__meter-reading-month {
  font-weight: 700;
  color: var(--lib__theme_secondary);
}
</style>
